import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  PublicClientApplication,
  AuthenticationResult,
  AuthError,
  AccountInfo,
} from '@azure/msal-browser';
import { msalConfig, loginRequest } from '../config/authConfig';

interface User {
  name: string;
  email: string;
  id: string;
}

export interface AuthContextType {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: User | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Single module-level instance, managed directly (no @azure/msal-react) — avoids the
// extra render/subscription layer that can lag behind the underlying MSAL state.
const msalInstance = new PublicClientApplication(msalConfig);

const toUser = (account: AccountInfo): User => ({
  name: account.name || account.username,
  email: account.username,
  id: account.localAccountId,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleResponse = useCallback((response: AuthenticationResult) => {
    if (!response?.account) return;
    msalInstance.setActiveAccount(response.account);
    setUser(toUser(response.account));
    setIsAuthenticated(true);
    setError(null);

    const savedUrl = sessionStorage.getItem('redirectUrl');
    if (savedUrl) {
      sessionStorage.removeItem('redirectUrl');
      window.location.replace(savedUrl);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const accounts = msalInstance.getAllAccounts();

    if (accounts.length > 0) {
      try {
        const response = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        handleResponse(response);
      } catch {
        // Silent refresh failed (e.g. token expired, needs interaction) — ProtectedRoute
        // will trigger a fresh loginRedirect.
        setIsAuthenticated(false);
        setUser(null);
      }
    } else {
      try {
        const response = await msalInstance.ssoSilent({ scopes: loginRequest.scopes });
        handleResponse(response);
      } catch {
        // No existing Microsoft session — user needs to log in manually.
        setIsAuthenticated(false);
        setUser(null);
      }
    }
  }, [handleResponse]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await msalInstance.initialize();
        const redirectResponse = await msalInstance.handleRedirectPromise();

        if (redirectResponse) {
          handleResponse(redirectResponse);
        } else {
          await checkAuth();
        }
      } catch (err) {
        if (err instanceof AuthError) {
          setError(`Authentication error: ${err.errorMessage}`);
        } else {
          setError('Authentication failed. Please try again.');
        }
      } finally {
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, [checkAuth, handleResponse]);

  const login = async () => {
    try {
      setError(null);
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath !== '/') {
        sessionStorage.setItem('redirectUrl', currentPath);
      }
      await msalInstance.loginRedirect(loginRequest);
    } catch (err) {
      if (err instanceof AuthError) {
        setError(`Login failed: ${err.errorMessage}`);
      } else {
        setError('Login failed. Please try again.');
      }
    }
  };

  const logout = async () => {
    try {
      setError(null);
      const account = msalInstance.getActiveAccount();
      if (account) {
        await msalInstance.logoutRedirect({ account, postLogoutRedirectUri: window.location.origin });
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (err) {
      if (err instanceof AuthError) {
        setError(`Logout failed: ${err.errorMessage}`);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isInitialized, user, login, logout, error }}>
      {children}
    </AuthContext.Provider>
  );
};
